import React, { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, H4, Small, TextInput } from "orcs-design-system";
import { useWorkflowBuilder } from "src/contexts/workflowBuilderContext";

// Category styling
const CATEGORY_CONFIG = {
  extract: {
    color: "#4CAF50",
    bgColor: "#E8F5E9",
    icon: "download",
    label: "Extract",
    description: "Read data from sources",
  },
  transform: {
    color: "#2196F3",
    bgColor: "#E3F2FD",
    icon: "exchange-alt",
    label: "Transform",
    description: "Process and modify data",
  },
  load: {
    color: "#FF9800",
    bgColor: "#FFF3E0",
    icon: "upload",
    label: "Load",
    description: "Write data to destinations",
  },
  control: {
    color: "#9C27B0",
    bgColor: "#F3E5F5",
    icon: "sliders-h",
    label: "Control",
    description: "Control workflow execution",
  },
};

const PaletteContainer = styled.div`
  width: ${(props) => (props.$isOpen ? "280px" : "0")};
  height: 100%;
  background: ${themeGet("colors.white")};
  border-right: 1px solid ${themeGet("colors.greyLighter")};
  overflow: hidden;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const PaletteHeader = styled.div`
  padding: ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const SearchContainer = styled.div`
  padding: ${themeGet("space.s")} ${themeGet("space.r")};
  border-bottom: 1px solid ${themeGet("colors.greyLighter")};
  flex-shrink: 0;
`;

const PaletteContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${themeGet("space.s")};
`;

const CategorySection = styled.div`
  margin-bottom: ${themeGet("space.r")};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${(props) => props.$bgColor || "#f5f5f5"};
  border-radius: ${themeGet("radii.2")};
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.9;
  }
`;

const CategoryIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${(props) => props.$color || "#666"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const CategoryTitle = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #333;
  flex: 1;
`;

const CategoryCount = styled.span`
  font-size: 11px;
  color: #666;
  background: white;
  padding: 2px 8px;
  border-radius: 10px;
`;

const ComponentList = styled.div`
  display: ${(props) => (props.$isExpanded ? "flex" : "none")};
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
`;

const ComponentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-radius: ${themeGet("radii.2")};
  cursor: grab;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.$color || "#666"};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateX(2px);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ComponentIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${(props) => props.$bgColor || "#f5f5f5"};
  color: ${(props) => props.$color || "#666"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const ComponentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ComponentName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ComponentDescription = styled.div`
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 48px;
  background: ${themeGet("colors.white")};
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${themeGet("colors.grey")};
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);

  &:hover {
    color: ${themeGet("colors.primary")};
  }
`;

const PaletteWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${themeGet("space.r")};
  color: ${themeGet("colors.grey")};
`;

// Component icons mapping
const COMPONENT_ICONS = {
  S3_CSV: "file-csv",
  S3_JSON: "file-code",
  GRAPHQL_QUERY: "project-diagram",
  API_REQUEST: "globe",
  LOCAL_FILE_CSV: "file-csv",
  LOCAL_FILE_JSON: "file-code",
  FIELD_MAPPER: "random",
  DATA_FILTER: "filter",
  DATA_TRANSFORMER: "magic",
  AGGREGATE: "layer-group",
  JOIN: "code-branch",
  CUSTOM_JS: "code",
  DB_UPSERT: "database",
  GRAPHQL_MUTATION: "project-diagram",
  NOTIFICATION: "bell",
  WEBHOOK: "paper-plane",
  CONDITIONAL_BRANCH: "code-branch",
  LOOP: "sync",
  APPROVAL_GATE: "user-check",
  DELAY: "clock",
  ERROR_HANDLER: "exclamation-triangle",
};

function ComponentPalette({ onDragStart }) {
  const { componentTypesByCategory, isSidebarOpen, toggleSidebar } =
    useWorkflowBuilder();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({
    extract: true,
    transform: true,
    load: true,
    control: true,
  });

  const toggleCategory = useCallback((category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  const handleDragStart = useCallback(
    (e, componentType) => {
      e.dataTransfer.setData(
        "application/reactflow",
        JSON.stringify(componentType)
      );
      e.dataTransfer.effectAllowed = "move";
      onDragStart?.(componentType);
    },
    [onDragStart]
  );

  // Filter components based on search
  const filteredCategories = Object.entries(componentTypesByCategory).reduce(
    (acc, [category, components]) => {
      if (!searchTerm) {
        acc[category] = components;
      } else {
        const filtered = components.filter(
          (comp) =>
            comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            comp.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            comp.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filtered.length > 0) {
          acc[category] = filtered;
        }
      }
      return acc;
    },
    {}
  );

  const hasResults = Object.keys(filteredCategories).length > 0;

  return (
    <PaletteWrapper>
      <PaletteContainer $isOpen={isSidebarOpen}>
        <PaletteHeader>
          <H4 mb="0">Components</H4>
          <Small color="grey">Drag to add</Small>
        </PaletteHeader>

        <SearchContainer>
          <TextInput
            id="component-search"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            iconLeft={["fas", "search"]}
          />
        </SearchContainer>

        <PaletteContent>
          {hasResults ? (
            Object.entries(filteredCategories).map(([category, components]) => {
              const config = CATEGORY_CONFIG[category] || {
                color: "#666",
                bgColor: "#f5f5f5",
                icon: "cog",
                label: category,
              };

              return (
                <CategorySection key={category}>
                  <CategoryHeader
                    $bgColor={config.bgColor}
                    onClick={() => toggleCategory(category)}
                  >
                    <CategoryIcon $color={config.color}>
                      <FontAwesomeIcon icon={["fas", config.icon]} />
                    </CategoryIcon>
                    <CategoryTitle>{config.label}</CategoryTitle>
                    <CategoryCount>{components.length}</CategoryCount>
                    <FontAwesomeIcon
                      icon={[
                        "fas",
                        expandedCategories[category]
                          ? "chevron-down"
                          : "chevron-right",
                      ]}
                      style={{ color: "#666", fontSize: 10 }}
                    />
                  </CategoryHeader>

                  <ComponentList $isExpanded={expandedCategories[category]}>
                    {components.map((comp) => (
                      <ComponentItem
                        key={comp.type}
                        $color={config.color}
                        draggable
                        onDragStart={(e) => handleDragStart(e, comp)}
                      >
                        <ComponentIcon
                          $bgColor={config.bgColor}
                          $color={config.color}
                        >
                          <FontAwesomeIcon
                            icon={["fas", COMPONENT_ICONS[comp.type] || "cog"]}
                          />
                        </ComponentIcon>
                        <ComponentInfo>
                          <ComponentName>{comp.name}</ComponentName>
                          {comp.description && (
                            <ComponentDescription>
                              {comp.description}
                            </ComponentDescription>
                          )}
                        </ComponentInfo>
                      </ComponentItem>
                    ))}
                  </ComponentList>
                </CategorySection>
              );
            })
          ) : (
            <NoResults>
              <Box mb="s">
                <FontAwesomeIcon
                  icon={["fas", "search"]}
                  style={{ fontSize: 24, opacity: 0.3 }}
                />
              </Box>
              <Small>No components found</Small>
            </NoResults>
          )}
        </PaletteContent>
      </PaletteContainer>

      <ToggleButton onClick={toggleSidebar}>
        <FontAwesomeIcon
          icon={["fas", isSidebarOpen ? "chevron-left" : "chevron-right"]}
        />
      </ToggleButton>
    </PaletteWrapper>
  );
}

ComponentPalette.propTypes = {
  onDragStart: PropTypes.func,
};

export default memo(ComponentPalette);
